import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { APP_NAME, APP_TAGLINE, STORAGE_KEYS } from '@/constants';
import { hybridStorage } from '@/utils/storage';
import { apiClient } from '@/api/client';
import type { ApiResponse } from '@/types/api';

export function applyFavicon(faviconUrl?: string) {
  if (typeof document === 'undefined') return;
  const url = faviconUrl?.trim() || './appicon.png';
  
  // Update or create standard icon
  let link: HTMLLinkElement | null = document.querySelector("link[rel='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = url;

  // Update shortcut icon if present
  let shortcutLink: HTMLLinkElement | null = document.querySelector("link[rel='shortcut icon']");
  if (!shortcutLink) {
    shortcutLink = document.createElement('link');
    shortcutLink.rel = 'shortcut icon';
    document.head.appendChild(shortcutLink);
  }
  shortcutLink.href = url;
}

interface BrandingState {
  siteName: string;
  tagline: string;
  logoUrl: string;
  faviconUrl: string;
  isLoaded: boolean;
  fetchBranding: (force?: boolean) => Promise<void>;
  setBranding: (branding: Partial<{ siteName: string; tagline: string; logoUrl: string; faviconUrl: string }>) => void;
}

export const useBrandingStore = create<BrandingState>()(
  persist(
    (set, get) => ({
      siteName: APP_NAME,
      tagline: APP_TAGLINE,
      logoUrl: '',
      faviconUrl: '',
      isLoaded: false,

      setBranding: (branding) => {
        set((state) => {
          const next = { ...state, ...branding };
          if (branding.faviconUrl !== undefined) {
            applyFavicon(branding.faviconUrl);
          }
          if (branding.siteName && typeof document !== 'undefined') {
            document.title = branding.siteName;
          }
          return next;
        });
      },

      fetchBranding: async (_force = false) => {
        try {
          const { data } = await apiClient.get<ApiResponse<any>>('/public/website-config');
          if (data?.success && data?.data) {
            const config = data.data;
            const updates = {
              siteName: config.siteName || APP_NAME,
              tagline: config.tagline || APP_TAGLINE,
              logoUrl: config.logoUrl || '',
              faviconUrl: config.faviconUrl || '',
              isLoaded: true,
            };
            get().setBranding(updates);
          }
        } catch {
          // Keep cached state
        }
      },
    }),
    {
      name: (STORAGE_KEYS as any).branding || 'society-branding',
      storage: createJSONStorage(() => hybridStorage),
      version: 1,
      partialize: (state) => ({
        siteName: state.siteName,
        tagline: state.tagline,
        logoUrl: state.logoUrl,
        faviconUrl: state.faviconUrl,
      }),
    },
  ),
);
